import React, { useContext, useEffect, useState } from "react";
import { AssignmentContext } from "context/Assignment";
import { getRangeForDate } from "./Calendar/Utils";
import { fromSqlDateTime, fromString } from "helpers/Date";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/solid";

export default function AssignmentCalendar(props) {
    const { queryParams, updateParams, items, loading } =
        useContext(AssignmentContext);
    const [range, setRange] = useState(() => {
        return [];
    });

    const parseRange = (r1, r2) => {
        const now = DateTime.fromFormat(r1, "yyyy-MM-dd", { zone: "local" });

        return {
            start: now.startOf("week").plus({ days: 6 }).startOf("day"),
            end: now.endOf("week").plus({ days: 6 }).endOf("day"),
        };
    };

    const getNextRange = (dt1, dt2) => {
        const r1 = dt1 < dt2 ? dt1 : dt2;
        const r2 = dt1 > dt2 ? dt1 : dt2;

        const nextStart = r1.plus({ weeks: 1 });
        const nextEnd = r2.plus({ weeks: 1 });

        return nextStart.toISODate() + "," + nextEnd.toISODate();
    };

    const getPrevRange = (dt1, dt2) => {
        const r1 = dt1 < dt2 ? dt1 : dt2;
        const r2 = dt1 > dt2 ? dt1 : dt2;

        const prevStart = r1.minus({ weeks: 1 });
        const prevEnd = r2.minus({ weeks: 1 });

        return prevStart.toISODate() + "," + prevEnd.toISODate();
    };

    const isWithinRange = (begin, end, item) => {
        const due = item.due_at ? fromString(item.due_at) : null;
        const start = item.action_start_time
            ? fromString(item.action_start_time)
            : null;

        if (due && due >= begin && due <= end) {
            return true;
        } else if (start && start >= begin && start <= end) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        if (queryParams.has("range")) {
            const { start, end } = parseRange(
                queryParams.get("range").split(",")[0],
                queryParams.get("range").split(",")[1]
            );
            setRange(getRangeForDate(start, end));
        } else {
            setRange(getRangeForDate("now"));
        }
    }, [queryParams]);

    return (
        <div className="space-y-4">
            <div className="flex items-center space-x-4">
                <div className="text-xl font-bold">
                    Week of {range[0]?.toFormat("DD")}
                </div>
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                onClick={() => {
                                    if (!range.length) {
                                        return;
                                    }
                                    updateParams({
                                        range: getPrevRange(range[0], range[6]),
                                    });
                                }}
                            >
                                <ArrowLeftIcon className="h-4 w-4" />
                                <span className="sr-only">Previous</span>
                            </button>,
                            <button
                                onClick={() => {
                                    if (!range.length) {
                                        return;
                                    }
                                    updateParams({
                                        range: getNextRange(range[0], range[6]),
                                    });
                                }}
                            >
                                <ArrowRightIcon className="h-4 w-4" />
                                <span className="sr-only">Next</span>
                            </button>,
                        ]}
                    />
                </div>
            </div>
            {loading ? (
                <div className="h-96 w-full animate-pulse bg-gray-200"></div>
            ) : (
                <div className="space-y-4 rounded-md bg-white pt-4 shadow-sm">
                    {range.map((date, idx) => (
                        <div
                            key={idx}
                            className="space-y-4 border-b border-gray-200 px-5 pb-4"
                        >
                            <div className="text-lg font-bold text-gray-600">
                                <span className="underline">
                                    {date.toLocal().toFormat("LLL d")}
                                </span>
                            </div>
                            {items
                                .filter((i) =>
                                    isWithinRange(
                                        date.toLocal().startOf("day"),
                                        date.toLocal().endOf("day"),
                                        i
                                    )
                                )
                                .sort((a, b) => {
                                    const t = date.toLocal().toISODate();
                                    if (
                                        fromSqlDateTime(
                                            a.action_start_time
                                        ).toISODate() === t
                                    ) {
                                        return a.action_start_time <
                                            b.action_start_time
                                            ? -1
                                            : 1;
                                    }

                                    return 0;
                                })
                                .map((item) => (
                                    <div key={item.id}>
                                        <Link
                                            to={`/app/planner/assignment/${item.uuid}`}
                                            className="flex items-center"
                                        >
                                            {date.toLocal().toISODate() ===
                                                fromSqlDateTime(
                                                    item.due_at
                                                ).toISODate() && (
                                                <span className="mr-4 rounded bg-yellow-400 px-2 py-0.5 text-xs text-yellow-900 shadow">
                                                    DUE
                                                </span>
                                            )}
                                            {date.toLocal().toISODate() ===
                                                fromSqlDateTime(
                                                    item.action_start_time
                                                ).toISODate() && (
                                                <span className="mr-4 rounded bg-blue-300 px-2 py-0.5 text-xs text-blue-900 shadow">
                                                    {fromSqlDateTime(
                                                        item.action_start_time
                                                    ).toFormat("h:mm a")}
                                                </span>
                                            )}
                                            <span className="text-green-600 underline">
                                                {item.title}
                                            </span>
                                        </Link>
                                    </div>
                                ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
